import type { Platform } from './types';

/**
 * PRD VS-050: `Esc` dismisses the window (TechSpec §10.3).
 *
 * One window-level listener in the bubble phase. A component that uses `Esc`
 * itself calls preventDefault(), so each press peels one layer: the menu
 * closes first, the window hides on the next press. IME composition keeps
 * its `Esc`. On web there is nothing to dismiss and nothing is installed.
 *
 * Returns the uninstaller.
 */
export function installEscapeToDismiss(
  platform: Platform,
  target: Pick<Window, 'addEventListener' | 'removeEventListener'> = window,
): () => void {
  const dismiss = platform.dismiss;
  if (!dismiss) return () => {};

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;
    if (event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) return;
    void dismiss.call(platform);
  };

  target.addEventListener('keydown', onKeyDown);
  return () => target.removeEventListener('keydown', onKeyDown);
}
